import { useState } from 'react';
import { 
  Container, Row, Col, 
  Form, Button, Card, 
  Alert, InputGroup, Spinner
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Layout from '../Dashbaord/Layout.js/Layout';
import UserService from '../../../services/user.services';
import { 
  FaUserPlus, FaIdCard, FaPhone, FaMapMarkerAlt, 
  FaBriefcase, FaUpload, FaTimes, FaMoneyBill,
  FaUserTag, FaCalendarAlt, FaBuilding
} from 'react-icons/fa';
import { colors } from '../../../utils/Colors';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CreateUser = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    cnic: '',
    phoneNumber: '',
    address: '',
    categories: 'Others',
    role: 'employee',
    joinedDate: new Date().toISOString().split('T')[0],
    salary: 0,
    isActive: true,
    employmentType: 'full-time',
    department: '',
    resigned: false
  });

  const [profilePicture, setProfilePicture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
               type === 'number' ? parseFloat(value) || 0 : 
               value
    }));
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size should be less than 2MB', {
          position: "bottom-right",
          autoClose: 3000,
        });
        return;
      }
      
      if (!file.type.match('image.*')) {
        toast.error('Please select an image file (JPG, PNG)', {
          position: "bottom-right",
          autoClose: 3000,
        });
        return;
      }
      
      setProfilePicture(file);
      setPreviewUrl(URL.createObjectURL(file));
      
      toast.success('Profile picture uploaded successfully!', {
        position: "bottom-right",
        autoClose: 2000,
      });
    }
  };
  
  const removeProfilePicture = () => {
    setProfilePicture(null);
    setPreviewUrl('');
    
    toast.info('Profile picture removed', {
      position: "bottom-right",
      autoClose: 2000,
    });
  };
  
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      toast.error('Name is required', {
        position: "bottom-right",
        autoClose: 3000,
      });
      return false;
    }
    
    if (!formData.cnic.trim()) {
      setError('CNIC is required');
      toast.error('CNIC is required', {
        position: "bottom-right",
        autoClose: 3000,
      });
      return false;
    }
    
    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      toast.error('Phone number is required', {
        position: "bottom-right",
        autoClose: 3000,
      });
      return false;
    }
    
    const cnicRegex = /^\d{5}-\d{7}-\d{1}$/;
    if (!cnicRegex.test(formData.cnic)) {
      setError('Please enter a valid CNIC format (xxxxx-xxxxxxx-x)');
      toast.error('Invalid CNIC format. Use XXXXX-XXXXXXX-X', {
        position: "bottom-right",
        autoClose: 3000,
      });
      return false;
    }
    
    const phoneRegex = /^(\+92|0)[0-9]{10}$/;
    if (!phoneRegex.test(formData.phoneNumber.replace(/\s+/g, ''))) {
      toast.error('Invalid phone number format. Use 923001234567 or 03001234567', {
        position: "bottom-right",
        autoClose: 3000,
      });
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const loadingToast = toast.loading('Creating employee...', {
        position: "bottom-right",
      });
      
      const userData = {
        ...formData,
        salary: parseFloat(formData.salary) || 0
      };
      
      const result = await UserService.createUser(userData, profilePicture);
      
      toast.dismiss(loadingToast);
      
      toast.success('✅ Employee created successfully!', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      setSuccess('Employee created successfully!');
      
      setTimeout(() => {
        navigate('/admin/dashboard/all-user');
      }, 1500);
      
    } catch (err) {
      toast.dismiss();
      
      const errorMessage = err.message || 'Failed to create employee. Please try again.';
      setError(errorMessage);
      
      toast.error(`❌ ${errorMessage}`, {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      console.error('Error creating employee:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      cnic: '',
      phoneNumber: '',
      address: '',
      categories: 'Others',
      role: 'employee',
      joinedDate: new Date().toISOString().split('T')[0],
      salary: 0,
      isActive: true,
      employmentType: 'full-time',
      department: '',
      resigned: false
    });
    
    setProfilePicture(null);
    setPreviewUrl('');
    setError('');
    setSuccess('');
    
    toast.info('Form has been reset', {
      position: "bottom-right",
      autoClose: 2000,
    });
  };
  
  const categories = ["Manager", "Sweeper", "Waiter", "Security", "Others"];
  const employmentTypes = ["full-time", "part-time", "contract", "temporary"];
  
  return (
    <Layout>
      <Container fluid className="py-4">
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="text-white py-3" style={{ backgroundColor : colors.TABLE_HEADERS}}>
            <Row className="align-items-center">
              <Col>
                <div className="d-flex align-items-center">
                  <div className="bg-white bg-opacity-25 p-3 rounded-circle me-3">
                    <FaUserPlus size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="fw-bold mb-1 text-white">Create New Employee</h2>
                    <p className="mb-0 text-white text-opacity-75">
                      Add a new employee to the system
                    </p>
                  </div>
                </div>
              </Col>
              <Col className="text-end">
                <Button 
                  variant="outline-light" 
                  onClick={() => navigate('/admin/dashboard/all-user')}
                  className="px-4 me-2"
                >
                  Back to List
                </Button>
                <Button 
                  variant="outline-warning" 
                  onClick={resetForm}
                  className="px-4"
                >
                  Reset Form
                </Button>
              </Col>
            </Row>
          </Card.Header>
        </Card>
        
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="border-0 shadow-sm mb-4">
            <FaTimes className="me-2" />
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} className="border-0 shadow-sm mb-4">
            <FaUserPlus className="me-2" />
            {success}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg={4}>
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-0 py-3">
                  <h6 className="mb-0 fw-bold">
                    <FaUpload className="me-2" />
                    Profile Picture
                  </h6>
                </Card.Header>
                <Card.Body className="text-center">
                  <div className="mb-3">
                    {previewUrl ? (
                      <div className="position-relative">
                        <img 
                          src={previewUrl} 
                          alt="Profile preview" 
                          className="rounded-circle border"
                          style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 rounded-circle"
                          onClick={removeProfilePicture}
                          style={{ width: '30px', height: '30px' }}
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    ) : (
                      <div className="border rounded-circle d-flex align-items-center justify-content-center mx-auto"
                        style={{ width: '150px', height: '150px', backgroundColor: '#f8f9fa' }}>
                        <FaUserPlus size={48} className="text-muted" />
                      </div>
                    )}
                  </div>
                  
                  <Form.Group controlId="profilePicture" className="mb-3">
                    <Form.Label className="small text-muted">Upload Profile Picture (Optional)</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="border-dashed"
                    />
                  </Form.Group>
                  
                  <p className="text-muted small mb-0">
                    Recommended: Square image, max 2MB, JPG/PNG format
                  </p>
                </Card.Body>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-0 py-3">
                  <h6 className="mb-0 fw-bold">
                    <FaUserTag className="me-2" />
                    Account Status
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Role</Form.Label>
                    <Form.Select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                    >
                      <option value="employee">Employee</option>
                      <option value="admin">Admin</option>
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="isActive"
                      name="isActive"
                      label="Active Account"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                    <Form.Text className="text-muted">
                      User will be active in the system
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={8}>
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-0 py-3">
                  <h6 className="mb-0 fw-bold">
                    <FaUserPlus className="me-2" />
                    Basic Information
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FaUserPlus />
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter full name"
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>CNIC <span className="text-danger">*</span></Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FaIdCard />
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            name="cnic"
                            value={formData.cnic}
                            onChange={handleInputChange}
                            placeholder="XXXXX-XXXXXXX-X"
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FaPhone />
                          </InputGroup.Text>
                          <Form.Control
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            placeholder="923001234567"
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Category</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FaUserTag />
                          </InputGroup.Text>
                          <Form.Select
                            name="categories"
                            value={formData.categories}
                            onChange={handleInputChange}
                          >
                            {categories.map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </Form.Select>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    <Col md={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FaMapMarkerAlt />
                          </InputGroup.Text>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            placeholder="Enter complete address"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-0 py-3">
                  <h6 className="mb-0 fw-bold">
                    <FaBriefcase className="me-2" />
                    Employment Details
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Department</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FaBuilding />
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            placeholder="e.g., HR, Finance, IT"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Employment Type</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FaCalendarAlt />
                          </InputGroup.Text>
                          <Form.Select
                            name="employmentType"
                            value={formData.employmentType}
                            onChange={handleInputChange}
                          >
                            {employmentTypes.map((type) => (
                              <option key={type} value={type}>
                                {type.replace('-', ' ').toUpperCase()}
                              </option>
                            ))}
                          </Form.Select>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Joined Date</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FaCalendarAlt />
                          </InputGroup.Text>
                          <Form.Control
                            type="date"
                            name="joinedDate"
                            value={formData.joinedDate}
                            onChange={handleInputChange}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-0 py-3">
                  <h6 className="mb-0 fw-bold">
                    <FaMoneyBill className="me-2" />
                    Salary Information
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Salary (PKR)</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FaMoneyBill />
                          </InputGroup.Text>
                          <Form.Control
                            type="number"
                            name="salary"
                            value={formData.salary}
                            onChange={handleInputChange}
                            placeholder=""
                            min=""
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              <div className="d-flex justify-content-end gap-3 mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/admin/dashboard/all-user')}
                  className="px-4"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  className="px-4"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaUserPlus className="me-2" />
                      Create Employee
                    </>
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </Layout>
  );
};

export default CreateUser;